import { Business } from "@turizi/shared";
import { Loader, Popover } from "@mantine/core";
import { IconBuildingStore, IconPlus, IconSelector } from "@tabler/icons-react";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { TAG_ICONS, getTagColor } from "@/lib/tag-utils";
import { BusinessToString } from "@/lib/models-utils";
import { multiWordSearch } from "@/utils/search-utils";
import CardModal from "./card-modal";
import CreateBusinessComponent from "./create-new-business-component";
import { useActiveBusiness } from "@/pages/wrapper";
import ForceBusinessData from "./force-business-data";

interface Props {
    onSelect: (business: Business) => void;
    preselected?: Business['id'] | null;
    className?: string;
    icon?: JSX.Element;
    clearOnSelect?: boolean;
}

export default function MyBusinessSelector({
    onSelect,
    className,
    clearOnSelect = false,
}: Props) {
    const { t } = useTranslation();
    const {
        activeBusiness,
        setActiveBusiness,
        businesses,
        isLoading
    } = useActiveBusiness();
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [opened, setOpened] = useState(false);
    const [createModalOpened, setCreateModalOpened] = useState(false);

    useEffect(() => {
        const defaultBiz = activeBusiness || (businesses.length === 1 ? businesses[0] : null);
        if (defaultBiz && !clearOnSelect) {
            onSelect(defaultBiz);
        }
    }, [activeBusiness?.id, businesses.length, clearOnSelect]);

    const filteredData = businesses.filter(item => {
        if (!searchQuery) return true;
        const query = searchQuery.toLowerCase();
        return multiWordSearch(BusinessToString(item, t), query);
    });

    const handleSelect = (item: Business) => {
        onSelect(item);
        setSearchQuery('');
        setOpened(false);
        if (clearOnSelect) {
            setActiveBusiness(null);
        } else {
            setActiveBusiness(item);
        }
    };

    const isSelected = (item: Business) => {
        return Number(activeBusiness?.id) === Number(item.id) && activeBusiness?.type === item.type;
    };

    return (
        <>
            {/* force buinsees data */}
            <ForceBusinessData />

            <Popover
                width={320}
                opened={opened}
                onChange={setOpened}
                position="bottom-start"
                offset={4}
                shadow="md"
                radius="md"
                trapFocus
                withinPortal
            >
                <Popover.Target>
                    <div
                        onClick={(e) => {
                            e.stopPropagation();
                            setOpened(!opened);
                        }}
                        className={`
                            cursor-pointer
                            ${className}
                            rounded-lg transition-colors duration-150
                            ${opened
                                ? 'bg-gray-100 hover:bg-gray-200/80'
                                : 'bg-transparent hover:bg-gray-100/80'
                            }
                            text-sm text-gray-800 font-medium
                            h-9 px-3
                            flex items-center gap-1.5 select-none
                        `}
                    >
                        <span>
                            {activeBusiness?.name || t('my_businesses')}
                        </span>
                        <IconSelector size={16} stroke={2} className="text-gray-700 flex-shrink-0" />
                    </div>
                </Popover.Target>

                <Popover.Dropdown
                    p={0}
                    className="overflow-hidden border border-gray-200/80 bg-white"
                    style={{ boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)', borderRadius: '10px' }}
                >
                    <div>
                        {/* Search Input Box */}
                        <div className="relative flex items-center px-3 py-2 border-b border-gray-100">
                            <input
                                autoFocus
                                type="text"
                                placeholder={t('find_project', 'Find Project...')}
                                className="w-full bg-transparent text-sm text-gray-900 placeholder-gray-400 focus:outline-none pr-12 font-medium"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                            <div className="absolute right-3 flex items-center pointer-events-none">
                                <span className="text-[10px] font-sans font-medium text-gray-400 bg-white border border-gray-200 shadow-sm px-1.5 py-0.5 rounded select-none">
                                    Esc
                                </span>
                            </div>
                        </div>

                        {/* Scrollable Items List */}
                        <div className="max-h-64 overflow-y-auto p-1.5 space-y-0.5">
                            {isLoading ? (
                                <div className="flex justify-center py-6">
                                    <Loader size="sm" color="gray" />
                                </div>
                            ) : filteredData.length === 0 ? (
                                <div className="text-center py-6 text-sm text-gray-500 font-medium">
                                    {t('no_results_found')}
                                </div>
                            ) : (
                                filteredData.map((item) => {
                                    const IconComponent = TAG_ICONS[item.category as keyof typeof TAG_ICONS] || IconBuildingStore;
                                    const color = getTagColor(item.category || '');
                                    const bgStyle = color.startsWith('#')
                                        ? { backgroundColor: `${color}18`, color: color }
                                        : { backgroundColor: '#f3f4f6', color: '#6b7280' };

                                    return (
                                        <div
                                            key={item.id}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleSelect(item);
                                            }}
                                            className={`
                                                flex items-center gap-3 px-3 py-2 rounded-md cursor-pointer transition-colors duration-150
                                                ${isSelected(item)
                                                    ? 'bg-gray-100/90 text-gray-900 font-medium'
                                                    : 'bg-transparent text-gray-700 hover:bg-gray-50 font-medium'
                                                }
                                            `}
                                        >
                                            <div
                                                style={bgStyle}
                                                className="w-7 h-7 rounded flex items-center justify-center flex-shrink-0"
                                            >
                                                <IconComponent size={16} stroke={1.5} />
                                            </div>
                                            <span className="text-sm truncate flex-1">
                                                {item.name}
                                            </span>
                                        </div>
                                    );
                                })
                            )}
                        </div>

                        {/* Footer Create Action */}
                        <div
                            onClick={(e) => {
                                e.stopPropagation();
                                setOpened(false);
                                setCreateModalOpened(true);
                            }}
                            className="flex items-center gap-2 px-4.5 py-3 border-t border-gray-100 cursor-pointer hover:bg-gray-50 text-sm font-medium text-gray-700 transition-colors duration-150"
                            style={{ paddingLeft: '18px', paddingRight: '18px' }}
                        >
                            <IconPlus size={16} stroke={2.5} className="text-gray-500" />
                            <span>{t('create_business', 'Create Business')}</span>
                        </div>
                    </div>
                </Popover.Dropdown>
            </Popover>

            {/* New Business Modal */}
            <CardModal
                size="fit"
                opened={createModalOpened}
                onClose={() => setCreateModalOpened(false)}
                header={
                    <div className="flex items-center gap-2 text-lg font-medium text-gray-800">
                        <IconBuildingStore size={18} stroke={2} className="text-gray-500" />
                        {t('create_business', 'Create Business')}
                    </div>
                }
                closeOnEscape={true}
            >
                <CreateBusinessComponent
                    onCreate={() => setCreateModalOpened(false)}
                    owner={true}
                />
            </CardModal>
        </>
    );
}